'use client';

import Image from 'next/image';

import ReactHtmlParser from 'react-html-parser';

import MuiBox from '@new-components/atoms/MuiBox';
import MuiContainer from '@new-components/atoms/MuiContainer';
import MuiTypography from '@new-components/atoms/MuiTypography';
import MuiButton from '@new-components/atoms/MuiButton';
import {SlickCarousel} from '@new-components/atoms/SlickCarousel';
import {MediaQuery} from '@constants/theme';

import {ImgProps} from '@custom-types/ImgProps';

type HighlightSlideProps = {
  tagLine: string;
  description: string;
  buttonLink: string;
  buttonLabel: string;
  image: ImgProps;
};
export type Props = {
  highlights: HighlightSlideProps[];
};

export const ANIMATION_TRANSITION_SPEED = 500;

export function TrendingHighlights(props: Props) {
  if (props.highlights.length === 1)
    return <HighlightSlide {...props.highlights[0]} />;

  const updatedHighlights = [...props.highlights]; //this is to counteract the shift in carousel after style update
  const firstItem = updatedHighlights.shift();
  const secondItem = updatedHighlights.shift();
  updatedHighlights.unshift(secondItem!!);
  updatedHighlights.push(firstItem!!);

  return (
    <MuiBox
      sx={{
        overflow: 'hidden',
        background: '#2D2D2D',
      }}>
      <MuiContainer
        sx={{
          padding: '24px',
          [MediaQuery.mobile]: {
            padding: '12px',
          },
        }}>
        <MuiBox
          sx={{
            'paddingBottom': '84px !important',
            'overflow': 'hidden',
            'width': 'calc(100% + 35vw)',
            [MediaQuery.mobile]: {
              width: 'calc(100% + 60vw)',
              paddingBottom: '48px',
            },

            '& .slick-dots': {
              bottom: '-48px !important',
              display: 'flex !important',
            },
            '& .slick-dots li, & .slick-dots li button': {
              height: '15px !important',
              width: '15px !important',
              marginInline: '0px !important',
            },
            '& .slick-dots li button:before': {
              fontSize: '13px !important',
              color: '#FFFFFF80 !important',
              height: '15px !important',
              width: '15px !important',
              opacity: '1 !important',
            },
            '& .slick-dots li.slick-active button:before': {
              color: '#FFFFFF !important', // Active dot color
              opacity: '1 !important',
            },
          }}>
          <SlickCarousel
            dots
            dotsClass='slick-dots'
            speed={ANIMATION_TRANSITION_SPEED}
            autoplay
            autoplaySpeed={4000}
            slidesToShow={2}
            centerMode
            centerPadding='0px'>
            {updatedHighlights.map((data: HighlightSlideProps, index) => (
              <HighlightSlide {...data} key={index} />
            ))}
          </SlickCarousel>
        </MuiBox>
      </MuiContainer>
    </MuiBox>
  );
}

function HighlightSlide(props: HighlightSlideProps) {
  return (
    <MuiBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        boxSizing: 'border-box',
        padding: '12px',
        [MediaQuery.mobile]: {
          padding: '12px',
        },
      }}>
      <MuiBox
        sx={{
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          minHeight: 'min(400px , 45vh)',
          boxSizing: 'border-box',
        }}>
        <Image
          layout='fill'
          objectFit='cover'
          src={props.image.src}
          alt={props.image.alt}
        />
      </MuiBox>
      <MuiBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '24px',
        }}>
        <MuiTypography
          sx={{
            fontSize: '22px',
            fontWeight: 300,
            lineHeight: '27.5px',
            letterSpacing: '-0.4px',
            color: '#ffffff',
            textAlign: 'left',
          }}>
          {ReactHtmlParser(props.tagLine)}
        </MuiTypography>
        <MuiTypography
          sx={{
            fontSize: '15px',
            fontWeight: 400,
            lineHeight: '22.5px',
            letterSpacing: '-0.4px',
            color: '#ffffff',
            textAlign: 'left',
            opacity: 0.6,
          }}>
          {ReactHtmlParser(props.description)}
        </MuiTypography>
        <MuiButton
          sx={{
            background: '#FFFFFF1F',
            backgroundBlendMode: 'color-dodge',
            fontSize: '15px',
            fontWeight: 400,
            lineHeight: '18.75px',
            letterSpacing: '-0.4px',
            textAlign: 'center',
            backdropFilter: 'blur(80px)',
            padding: '12px 18px',
          }}
          href={props.buttonLink}>
          {props.buttonLabel}
        </MuiButton>
      </MuiBox>
    </MuiBox>
  );
}
